import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getManageWeaponGroups,
  getPortalWeaponGroups,
  updateManageWeaponGroup,
} from '@api/shootingApi';
import { IUpdateWeaponGroupDefinitionParams } from '@api/shootingApi/interfaces';
import { buildWeaponGroupCategoryMap } from '@helpers/clearanceCategories';
import { IPaginatedResourceEndpointResponse } from '@api/interfaces';
import i18n from '@translations/i18n';
import { WeaponGroupDefinition } from '@api/shootingApi/interfaces';
import { IPaginatedWeaponGroupsReactQueryParams } from './interfaces';
import { getWeaponGroupShortLabel } from '@helpers/weaponGroups';
import { WeaponGroup } from '@api/disciplinesApi/interfaces';

export const usePortalWeaponGroupsQuery = (enabled = true) => {
  return useQuery({
    queryKey: ['portal', 'weapon-groups'],
    queryFn: getPortalWeaponGroups,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useManageWeaponGroupsQuery = (enabled = true) => {
  return useQuery({
    queryKey: ['manage', 'weapon-groups'],
    queryFn: getManageWeaponGroups,
    enabled,
  });
};

export const useUpdateManageWeaponGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: IUpdateWeaponGroupDefinitionParams;
    }) => updateManageWeaponGroup(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manage', 'weapon-groups'] });
      queryClient.invalidateQueries({ queryKey: ['mng-weapon-groups'] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'weapon-groups'] });
    },
  });
};

export const useGetManageWeaponGroupsTable = ({
  page = 0,
  pageSize,
  sortField,
  search,
  enabled = true,
}: IPaginatedWeaponGroupsReactQueryParams) => {
  return useQuery<IPaginatedResourceEndpointResponse<WeaponGroupDefinition>>({
    queryKey: ['mng-weapon-groups', page, pageSize, sortField, search],
    queryFn: async () => {
      let rows = await getManageWeaponGroups();

      if (search?.trim()) {
        const query = search.trim().toLowerCase();

        rows = rows.filter((definition) => {
          const codeLabel = getWeaponGroupShortLabel(
            definition.code as WeaponGroup
          ).toLowerCase();
          const descriptionLabel = i18n
            .t(`competitions:weaponGroups.${definition.code as WeaponGroup}.label`)
            .toLowerCase();
          const categoryLabel = i18n
            .t(`shooting:clearanceCategories.${definition.clearanceCategory}`)
            .toLowerCase();

          return (
            definition.code.toLowerCase().includes(query) ||
            codeLabel.includes(query) ||
            descriptionLabel.includes(query) ||
            categoryLabel.includes(query)
          );
        });
      }

      if (sortField) {
        const direction = sortField.sort === 'desc' ? -1 : 1;

        rows = [...rows].sort((left, right) => {
          switch (sortField.field) {
            case 'code':
              return left.code.localeCompare(right.code) * direction;
            case 'clearanceCategory':
              return (
                left.clearanceCategory.localeCompare(right.clearanceCategory) *
                direction
              );
            case 'sortOrder':
              return (left.sortOrder - right.sortOrder) * direction;
            default:
              return 0;
          }
        });
      }

      const total = rows.length;
      const perPage =
        pageSize === 'all' ? Math.max(total, 1) : pageSize;
      const start = page * perPage;
      const data = rows.slice(start, start + perPage);

      return {
        data,
        meta: {
          currentPage: page + 1,
          lastPage: Math.max(1, Math.ceil(total / perPage)),
          perPage,
          total,
        },
      };
    },
    enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const usePortalWeaponGroupCategoryMap = (enabled = true) => {
  const query = usePortalWeaponGroupsQuery(enabled);

  return {
    ...query,
    categoryMap: query.data
      ? buildWeaponGroupCategoryMap(query.data)
      : undefined,
  };
};

export const useManageWeaponGroupCategoryMap = (enabled = true) => {
  const query = useManageWeaponGroupsQuery(enabled);

  return {
    ...query,
    categoryMap: query.data
      ? buildWeaponGroupCategoryMap(query.data)
      : undefined,
  };
};
